import React, { useCallback, useEffect, useMemo, useState } from "react";
import pluralize from "pluralize";
import upperFirst from "lodash/upperFirst";
import useQueryString from "use-query-string";
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Flex,
  Grid,
  HStack,
  Heading,
  Hide,
  IconButton,
  Show,
  Stack,
  Text,
  chakra,
} from "@chakra-ui/react";

import { BsSortDown, BsSortUp } from "react-icons/bs";
import { CONTAINER_PADDING_X, hasCourseAccess, useUser } from "../utils";
import {
  FilterContext,
  TutorialCard,
  TutorialsFilter,
  TutorialsFiltersDrawer,
  TutorialsSearchInput,
} from "@components/Tutorials";
import { Footer } from "@components/Footer";
import { GatsbySeo } from "gatsby-plugin-next-seo";
import { Helmet } from "react-helmet-async";
import { HomePageHeader } from "@components/HomePage";
import { PageProps, graphql, navigate } from "gatsby";
import { PageSeo } from "@components/PageSeo";
import { orderBy } from "lodash";
import { popularitySortOptions } from "../utils/sort";

const meta = {
  title: "GraphQL tutorials and resources",
  description: "Search, filter, and browse our catalog of GraphQL tutorials.",
};

export default function BrowseTutorials({
  data: { site, allCourse, allCertification },
  location,
}: PageProps<Queries.ListTutorialsQuery>) {
  const [sort, setSort] = useState<"popularity" | "title" | "date">(
    "popularity"
  );
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  const reverseOrder = useMemo(
    () => (order === "asc" ? "desc" : "asc"),
    [order]
  );

  const [search, setSearch] = useState("");
  const clearSearch = useCallback(() => setSearch(""), []);

  const queryStringResult = useQueryString(location, (path) => {
    navigate(path.replace(new RegExp(`^${site?.pathPrefix}`), ""), {
      replace: true,
      state: {
        disableScrollUpdate: true,
      },
    });
  });

  const query = queryStringResult[0];
  const setQuery = queryStringResult[1];

  const clearFilter = useCallback(
    () =>
      setQuery({
        types: [],
        certification: [],
        categories: [],
      }),
    [setQuery]
  );

  const { user } = useUser();

  const completedTasks = useMemo(() => {
    const tasks = user?.tasks ?? [];
    return tasks.filter((task) => task.completedAt).map((task) => task.id);
  }, [user]);

  const tutorialsWithAccess = useMemo(
    () =>
      allCourse.nodes.filter(
        ({ memberships, tiers }) =>
          hasCourseAccess({
            user,
            memberships,
            tiers,
          }) || tiers?.includes("ENTERPRISE")
      ),
    [allCourse.nodes, user]
  );

  const hasFilters = useMemo(
    () => Object.entries(query).some(([, value]) => value?.length),
    [query]
  );

  const filteredTutorials = useMemo(() => {
    // first, check if any filters or search are set
    const trimmedSearch = search.trim();
    if (!trimmedSearch && !hasFilters) {
      // exit early if no filters set
      return tutorialsWithAccess;
    }

    // otherwise, work through filters and return a subset of tutorials
    return tutorialsWithAccess.filter((tutorial) => {
      const { categories, types, certifications, languages, levels, paths } =
        query;

      if (certifications) {
        const certificationsFilter = Array.isArray(certifications)
          ? certifications
          : [certifications];
        if (certificationsFilter.length) {
          const criteria = allCertification.nodes
            .filter((certification) =>
              certificationsFilter.includes(certification.slug)
            )
            .flatMap((certification) => certification.criteria);

          if (!criteria.includes(tutorial.progressId)) {
            return false;
          }
        }
      }

      if (categories) {
        const categoriesFilter = Array.isArray(categories)
          ? categories
          : [categories];
        if (
          categoriesFilter.length &&
          !tutorial.categories?.some(
            (category) => category && categoriesFilter.includes(category)
          )
        ) {
          return false;
        }
      }

      if (types) {
        const typesFilter = Array.isArray(types) ? types : [types];
        if (
          typesFilter.length &&
          ![...typesFilter]
            .map((type) => (type === "course" ? null : type))
            .includes(tutorial.type)
        ) {
          return false;
        }
      }

      if (languages) {
        const languagesFilter = Array.isArray(languages)
          ? languages
          : [languages];
        if (
          languagesFilter.length &&
          !tutorial.languages?.some(
            (language) => language && languagesFilter.includes(language)
          )
        ) {
          return false;
        }
      }

      if (levels) {
        const levelsFilter = Array.isArray(levels) ? levels : [levels];
        if (
          levelsFilter.length &&
          !levelsFilter.some((level) => level === tutorial.educationLevel)
        ) {
          return false;
        }
      }

      if (paths) {
        const pathsFilter = Array.isArray(paths) ? paths : [paths];
        if (
          pathsFilter.length &&
          !tutorial.paths?.some((path) => path && pathsFilter.includes(path))
        ) {
          return false;
        }
      }

      if (trimmedSearch.length) {
        const lowerSearch = trimmedSearch.toLowerCase();
        const { title, description } = tutorial;
        if (
          !title?.toLowerCase().includes(lowerSearch) &&
          !description?.toLowerCase().includes(lowerSearch)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [hasFilters, tutorialsWithAccess, query, search, allCertification.nodes]);

  const sortedTutorials = useMemo(
    () =>
      orderBy(
        filteredTutorials,
        sort === "popularity" ? popularitySortOptions : sort,
        [sort === "title" ? reverseOrder : order, reverseOrder]
      ),
    [filteredTutorials, sort, order, reverseOrder]
  );

  const types = useMemo(() => {
    const uniqueTypes = [
      ...new Set(
        tutorialsWithAccess
          .map((tutorial) => tutorial.type)
          .filter((type): type is string => type !== null)
      ),
    ].sort();
    return [
      { value: "course", label: "Course" },
      ...uniqueTypes.map((value) => ({
        value,
        label: upperFirst(value),
      })),
    ];
  }, [tutorialsWithAccess]);

  const categories = useMemo(() => {
    const uniqueCategories = [
      ...new Set(
        tutorialsWithAccess
          .flatMap((tutorial) => tutorial.categories)
          .filter((tutorial): tutorial is string => tutorial !== null)
      ),
    ].sort();
    return uniqueCategories.map((value) => ({
      value,
      label: value,
    }));
  }, [tutorialsWithAccess]);

  const paths = useMemo(() => {
    const uniquePaths = [
      ...new Set(
        tutorialsWithAccess
          .flatMap((tutorial) => tutorial.paths)
          .filter((path): path is string => path !== null)
      ),
    ].sort();
    return uniquePaths.map((value) => ({
      value,
      label: upperFirst(value),
    }));
  }, [tutorialsWithAccess]);

  const codeLanguages = useMemo(() => {
    const uniqueCodeLanguages = [
      ...new Set(
        tutorialsWithAccess
          .flatMap((tutorial) => tutorial.languages)
          .filter((language): language is string => language !== null)
      ),
    ].sort();
    return uniqueCodeLanguages.map((value) => ({
      value,
      label: upperFirst(value),
    }));
  }, [tutorialsWithAccess]);

  const levels = useMemo(() => {
    const uniqueTypes = [
      ...new Set(
        tutorialsWithAccess
          .map((tutorial) => tutorial.educationLevel)
          .filter((level): level is string => level !== null)
      ),
    ];
    return uniqueTypes.map((value) => ({
      value,
      label: upperFirst(value),
    }));
  }, [tutorialsWithAccess]);

  const filters = useMemo(
    () => (
      <>
        <Flex align="baseline" mb="8">
          <Heading size="lg" as="h3">
            Filter results
          </Heading>
          {hasFilters && (
            <Show above="sm">
              <Button
                ml="3"
                size="lg"
                variant="link"
                colorScheme="indigo"
                onClick={clearFilter}
              >
                Clear
              </Button>
            </Show>
          )}
        </Flex>
        <Stack spacing="6">
          <FilterContext.Provider
            value={{
              filter: query,
              setFilter: setQuery,
            }}
          >
            <TutorialsFilter
              title="Coding language"
              filterKey="languages"
              options={codeLanguages}
            />
            <TutorialsFilter title="Paths" filterKey="paths" options={paths} />
            <TutorialsFilter
              title="Experience level"
              filterKey="levels"
              options={levels}
            />
            <TutorialsFilter
              title="Tutorial type"
              filterKey="types"
              options={types}
            />
            <TutorialsFilter
              title="Certification"
              filterKey="certifications"
              options={allCertification.nodes.map((certification) => ({
                value: certification.slug,
                label: certification.title || "",
              }))}
            />
            <TutorialsFilter
              title="Categories"
              filterKey="categories"
              options={categories.map((category) => ({
                value: category.value,
                label: upperFirst(category.label),
              }))}
            />
          </FilterContext.Provider>
        </Stack>
      </>
    ),
    [
      allCertification.nodes,
      query,
      setQuery,
      categories,
      hasFilters,
      types,
      codeLanguages,
      paths,
      levels,
      clearFilter,
    ]
  );

  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    setNow(Date.now());
  }, []);

  // build out itemListElement arr for the our structured data

  const itemListElement = tutorialsWithAccess.map((course, index) => {
    return {
      "@type": "ListItem",
      position: index + 1,
      url: `https://apollographql.com/tutorials/${course.slug}`,
    };
  });

  const itemListJsonLd = {
    "@type": "ItemList",
    itemListElement,
  };

  const courseListJsonLd = tutorialsWithAccess.map((course) => {
    return {
      "@type": "Course",
      name: course.title,
      description: course.description,
      provider: {
        "@type": "Organization",
        name: "Apollo GraphQL",
        sameAs: "https://apollographql.com",
      },
    };
  });

  const JsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [itemListJsonLd, ...courseListJsonLd],
  });

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JsonLd}</script>
      </Helmet>
      <GatsbySeo canonical="https://www.apollographql.com/tutorials/browse" />
      <PageSeo {...meta} />
      <HomePageHeader />
      <Box
        pt={{ base: 6, lg: 12 }}
        pb="14"
        px={CONTAINER_PADDING_X}
        maxW="container.xl"
        mx="auto"
      >
        <Heading mb="2" size="2xl">
          {meta.title}
        </Heading>
        <Text fontSize="lg">{meta.description}</Text>
        <Flex mt={{ base: 10, md: 12, lg: 16 }}>
          <Hide below="md">
            <chakra.aside
              key={now}
              flexShrink={0}
              mr="8"
              w={{ base: 231, lg: 304 }}
            >
              {filters}
            </chakra.aside>
          </Hide>
          <chakra.main flexGrow={1}>
            <Hide above="md">
              <TutorialsFiltersDrawer
                hasFilters={hasFilters}
                clearFilter={clearFilter}
              >
                {filters}
              </TutorialsFiltersDrawer>
            </Hide>
            <TutorialsSearchInput
              clear={clearSearch}
              onChange={(event) => setSearch(event.target.value)}
              value={search}
            />
            {sortedTutorials.length ? (
              <>
                <Flex my="4" align="center" justify="space-between">
                  <span>
                    {pluralize("result", sortedTutorials.length, true)}
                  </span>
                  <HStack>
                    <span>Sort by:</span>
                    <ButtonGroup size="sm" isAttached>
                      <Button
                        colorScheme={sort === "popularity" ? "blue" : "gray"}
                        mr="px"
                        onClick={() => setSort("popularity")}
                      >
                        Popularity
                      </Button>
                      <Button
                        colorScheme={sort === "title" ? "blue" : "gray"}
                        mr="px"
                        onClick={() => setSort("title")}
                      >
                        Title
                      </Button>
                      <Button
                        colorScheme={sort === "date" ? "blue" : "gray"}
                        mr="px"
                        onClick={() => setSort("date")}
                      >
                        Updated
                      </Button>
                      <IconButton
                        aria-label="Toggle sort order"
                        icon={order === "asc" ? <BsSortUp /> : <BsSortDown />}
                        onClick={() => setOrder(reverseOrder)}
                      />
                    </ButtonGroup>
                  </HStack>
                </Flex>
                <Grid
                  key={now}
                  gap="4"
                  templateColumns="repeat(auto-fill, minmax(250px, 1fr))"
                >
                  {sortedTutorials.map((tutorial) => (
                    <TutorialCard
                      key={tutorial.id}
                      tutorial={tutorial}
                      search={search}
                      completedTasks={completedTasks}
                      pageSource="browse"
                      isBrowseFeatured={
                        tutorial.slug === "connectors-intro-rest"
                      }
                    />
                  ))}
                </Grid>
              </>
            ) : (
              <Center
                textAlign="center"
                py={{ base: 8, md: 12 }}
                maxW={400}
                mx="auto"
              >
                <div>
                  <Heading mb="2">
                    No results found{" "}
                    <span role="img" aria-label="pensive face">
                      😔
                    </span>
                  </Heading>
                  <Text>
                    Looks like we don&apos;t have anything that matches your
                    query. Try expanding your filters and/or search terms.
                  </Text>
                </div>
              </Center>
            )}
          </chakra.main>
        </Flex>
      </Box>
      <Footer />
    </>
  );
}

export const pageQuery = graphql`
  query ListTutorials {
    site {
      pathPrefix
    }
    allCertification(filter: { unlisted: { ne: true } }) {
      nodes {
        id
        slug
        title
        criteria
      }
    }
    allCourse(filter: { unlisted: { ne: true } }) {
      nodes {
        id
        slug
        title
        description
        authors {
          name
          image
        }
        thumb {
          src
          alt
        }
        categories
        languages
        paths
        educationLevel
        memberships
        tiers
        preview
        status
        type
        date
        progressId
        version
        lessons {
          tasks {
            id
          }
          frontmatter {
            time
            videoId
          }
        }
      }
    }
  }
`;
